import { AssetTypes } from '@tassat/common-definitions';
import { Asset } from '@tassat/fred-definitions';
import { AllowedTransactionType, AssetWithPrice, SecurityCurrencyAssetPair } from '@tassat/fred-extended-definitions';
import { createSelector } from 'reselect';

import { assetSort } from 'Lib/stringHelpers';
import { AdminState } from 'Reducers/store/adminStore';
import { ClientState } from 'Reducers/store/clientStore';

import { getAssets, getPrices } from './common';
import { getFirmAssetEntitlementsByFirmId } from './firmAssetEntitlements';

const getAssetTicker = (_state: AdminState | ClientState, ticker: string | undefined) => ticker;

export const getAllAssetsArray = createSelector(getAssets, (assets): Asset[] => (
    Object.keys(assets)
        .map(id => assets[id])
        .sort(assetSort)
));

type AssetWithPriceMap = {
    [key: string]: AssetWithPrice;
};

export interface AssetsByCurCodeAndPrice {
    curCode: string;
    symbol: string;
    ccyAssets: AssetWithPriceMap;
    securityAssets: AssetWithPriceMap;
}

export const getHistoryExportAssets = createSelector([getAssets], (assets) => {
    return Object.values(assets)
        .reduce((acc, asset) => {
            acc[asset.AssetId] = {
                AssetId: asset.AssetId,
                Type: asset.Type,
                Symbol: asset.Symbol,
                Ticker: asset.Ticker,
                Curcode: asset.Curcode,
                DecimalPoint: asset.DecimalPoint
            };

            return acc;
        }, {} as {
            [key: number]: Partial<Asset>;
        });
});

export const getAllAssetsArrayWithPrice = createSelector([ getAssets, getPrices ], (assets, prices): AssetWithPrice[] => {
    const response = Object.values(assets)
        .reduce((acc, asset) => {
            if (Object.prototype.hasOwnProperty.call(prices, asset.AssetId)) {
                acc.push({
                    ...asset,
                    Price: prices[asset.AssetId].Price
                });
            } else {
                acc.push(asset);
            }

            return acc;
        }, [] as AssetWithPrice[]);

    return response;
});

export const getAllAssetsByCurCodeAndPriceArray = createSelector([getAllAssetsArrayWithPrice], (assets): AssetsByCurCodeAndPrice[] => {
    const assetsByCurCode = assets.reduce((acc, asset) => {
        if (acc[asset.Curcode]) {
            acc[asset.Curcode].assets.push(asset);
        } else {
            acc[asset.Curcode] =
                {
                    symbol: asset.Symbol,
                    assets: [{ ...asset }]
                };
        }

        return acc;
    }, {} as {
        [key: string]: { symbol: string; assets: AssetWithPrice[] };
    });

    const assetsByCurCodeArray = Object.entries(assetsByCurCode)
        .map(([ curCode, assetsObj ]) => ({
            curCode,
            symbol: assetsObj.symbol,
            ccyAssets: assetsObj.assets.reduce((acc, item) => (item.Type === AssetTypes.CURRENCY ? {
                ...acc,
                [item.AssetId]: item
            } : acc), {} as AssetWithPriceMap),
            securityAssets: assetsObj.assets.reduce((acc, item) => (item.Type === AssetTypes.ASSET ? {
                ...acc,
                [item.AssetId]: item
            } : acc), {} as AssetWithPriceMap)
        }));

    return assetsByCurCodeArray;
});

export const getAssetFromTicker = createSelector([ getAllAssetsArray, getAssetTicker ],
    (assets, ticker): Asset => assets.find((asset) => asset.Ticker === ticker));

export const getSecurityCurrencyPairBySecurityTicker = createSelector(
    [ getAllAssetsArray, getAssetTicker ],
    (assets, ticker): SecurityCurrencyAssetPair => {
        if (!ticker) {
            return {};
        }

        const matchedAsset = assets.find((asset) => asset.Ticker === ticker);

        if (!matchedAsset) {
            return {};
        }

        if (matchedAsset.Type === AssetTypes.CURRENCY) {
            return { currency: matchedAsset };
        }

        return {
            security: matchedAsset,
            currency: assets.find((asset) => asset.Ticker === matchedAsset.Curcode)
        };
    }
);

export const getAllSecurityCurrencyPairs = createSelector(
    [getAllAssetsArrayWithPrice],
    (assets): SecurityCurrencyAssetPair[] => assets
            .filter(a => a.Type === AssetTypes.ASSET)
            .map(security => ({
                security,
                currency: assets.find(a => a.Ticker === security.Curcode)
            }))
);

export const getAssetsMap = createSelector(
    getAssets,
    (assets): Record<string, Asset> => assets
);

export const getEntitledAssetsByTransactionType = createSelector(
    [ getAllAssetsArray, getFirmAssetEntitlementsByFirmId ],
    (assets, entitlements): Record<AllowedTransactionType, Asset[]> => {
        const allowedTickers = new Set(entitlements.map(entitlement => entitlement.Ticker));
        const entitledAssets = assets.filter(asset => allowedTickers.has(asset.Ticker));

        return Object.values(AllowedTransactionType)
            .reduce((acc, transactionType) => ({
                ...acc,
                [transactionType]: entitledAssets.filter(asset => asset.AllowedTransactions?.split(',')
                    .map(t => t.trim())
                    .includes(transactionType))
            }), {} as Record<AllowedTransactionType, Asset[]>);
    }
);